import { useCallback } from 'react';

import { useShowConsumerCommunicationStatus } from '@givebacks/api/backer';
import { IfFeatureState_Core } from 'core';
import { Box, LoadingOverlay, Modal, Stack, Text, TransitionElement } from 'ui';
import { CauseCard } from '~/pages/Communications/components/CauseCard';
import { CauseFollowCard } from '~/pages/Communications/components/CauseFollowCard';

import {
  ModalTitleWithBackButton,
  useCommunicationListsModalProvider,
} from './CommunicationListsProvider';
import { Communications } from './OrgInfoContent/Communications';
import { OrgInfoContent } from './OrgInfoContent/OrgInfoContent';

const CauseNotAvailable = () => {
  return (
    <Stack gap={4}>
      <Text fz="lg" fw={600}>
        Unable to load this organization’s details.
      </Text>
      <Text fz="sm" c="gray">
        If the issue continues, reach out to support.
      </Text>
    </Stack>
  );
};

const CauseStatusModalContent = () => {
  const { meta, openNextStep, setMeta, openModalById } = useCommunicationListsModalProvider({
    id: 'CauseStatus',
  });
  const { data, isLoading, isError } = useShowConsumerCommunicationStatus({
    causeId: meta?.causeId,
  });

  const status = data?.communication_status;

  const handleOpenUnfollowModal = useCallback(() => {
    setMeta({ causeId: meta?.causeId });
    openModalById('Unfollow');
  }, [setMeta, meta?.causeId, openModalById]);

  return (
    <>
      {isLoading && (
        <Box h="120">
          <LoadingOverlay visible />
        </Box>
      )}
      <TransitionElement mounted={!isLoading}>
        {isError ? (
          <CauseNotAvailable />
        ) : (
          <>
            <Stack>
              <IfFeatureState_Core
                featureId="user.account_page"
                fallBack={
                  <CauseCard
                    causeId={meta?.causeId}
                    status={data?.communication_status}
                    fullWidth
                    onUnsubscribe={openNextStep}
                  />
                }
              >
                <CauseFollowCard
                  causeId={meta?.causeId}
                  fullWidth
                  onOpenUnfollowModal={handleOpenUnfollowModal}
                />
              </IfFeatureState_Core>
              <IfFeatureState_Core
                featureId="user.account_page"
                fallBack={<Communications causeId={meta?.causeId ?? ''} status={status ?? ''} />}
              >
                <OrgInfoContent causeId={meta?.causeId ?? ''} />
              </IfFeatureState_Core>
            </Stack>
          </>
        )}
      </TransitionElement>
    </>
  );
};

const CauseStatusModal = ({ title }: { title?: string }) => {
  const { opened, close, props } = useCommunicationListsModalProvider({ id: 'CauseStatus' });
  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>{title ?? 'Lists'}</ModalTitleWithBackButton>}
      {...props}
    >
      <CauseStatusModalContent />
    </Modal>
  );
};

export { CauseStatusModal };
